import _ from 'lodash';
import {
    env,
    localStorage,
    jsonCopy,
    exposed,
    emerge
} from '@fiverr-private/futile';
import { stats } from '@fiverr-private/obs';

import {
    STORAGE_KEY,
    CUSTOM_EVENT,
    COLLECT_DELAY,
    VIEWED_ATTRIBUTE,
    WINDOW_LISTENERS,
    LISTENER_OPTIONS
} from './constants';
import { dispatch } from './dispatch';
import { sendBigQueryEvents } from './sendEvents';

/**
 * @typedef ImpressionableOptions
 * @type {Object}
 * @property {Object[]}             [data=[]]                         Array of BI data objects that will be send to BigQuery upon impression. Each item must have a unique identifier.
 * @property {HTMLElement}          [container=window]                The impressionable's container.
 * @property {String}               [DOMSelector=`[${DOMAttribute}]`] Query selector used to fetch the impressionable's children.
 * @property {String}               [excludeDOMSelector=`[${DOMAttribute}]`] Query selector used to fetch and exclude impressionable's children.
 * @property {String}               [DOMAttribute='id']               DOM attribute holding the item's identifier. Will be matched against the item's "dataIdentifier".
 * @property {String}               [dataIdentifier='id']             Data member holding the item's identifier. Will be matched against the item's "DOMAttribute".
 * @property {String}               [dataProperty]                    Optional key to pick out of the "data" item, instead of sending the entire item.
 * @property {EnrichLambda}         [enrich]
 * @property {OnImpressionCallback} [onImpression]
 */

/**
 * This callback will be called each time an impression event was reported.
 * @callback OnImpressionCallback
 * @param    {Object}                 event    The event reported impression..
 * @returns  {undefined}
 *
 * @example
 * // Log each time an impression event was reported.
 * new Impressionable({
 *   onImpression: (event) => { console.log('A new impression event was reported!'); }
 * })
 */

/**
 * A lambda expression with privileged access to the event data item. Is meant to mutate it (usually add properties).
 * @callback EnrichLambda
 * @param    {Object}                 data    The event data item (mutable).
 * @param    {HTMLElement}            [child] The element corresponding to the DOMSelector-DOMAttribute-dataIdentifier combination.
 * @returns  {Object|undefined|false}         Optionally: return a new event object. Returning false will prevent the send of the impression
 *
 * @example
 * // Add a new item to the event
 * new Impressionable({
 *   enrich: (data) => { data.source_url = window.location.href; }
 * })
 *
 * // Replace event
 * new Impressionable({
 *   enrich: (data, element) => Object.assign(
 *     {},
 *     data,
 *     {
 *       type: 'some_other_type',
 *       position: data.$index + 1,
 *       url: window.location.href,
 *       offsetTop: element.offsetTop,
 *     }
 *   )
 * })
 */

/**
 * Options defaults
 * @type {ImpressionableOptions}
 */
const defaults = {
    data: [],
    container: typeof window !== 'undefined' ? window : null,
    DOMAttribute: 'id',
    dataIdentifier: 'id'
};

/**
 * @class Impressionable
 * @classdesc An impression tracker; listens to UI events and reports the "impressed" (i.e. seen) children to BigQuery.
 * @param  {ImpressionableOptions}   options
 * @property {ImpressionableOptions} options
 * @property {HTMLElement[]}         impressed   A list of all elements that were "impressed", i.e. seen.
 * @property {HTMLElement[]}         unimpressed A list of all elements yet to be "impressed".
 * @property {Boolean}               retired     Whether this instance is "retired" or not, i.e. all children have been "impressed"
 *
 * @example
 * const impressionable = new Impressionable({
 *   data: [{id: 0, name: 'a1vy', impression_data: {id: 0, group: 'event_group', type: 'event_type'}}],
 *   container: document.getElementById('my_container'),
 *   DOMSelector: '.card',
 *   DOMAttribute: 'data-impression-id',
 *   dataIdentifier: 'id',
 *   dataProperty: 'impression_data',
 *   enrich: (data, element) => Object.assign(data, {
 *     position: data.$index + 1,
 *     url: window.location.href,
 *     offsetTop: element.offsetTop,
 *   }),
 *   onImpression: (event) => {
 *     console.log('A new impression event was reported!');
 *   }
 * })
 *
 * // Triggering after custom scrolling
 * container.addEventListener('transitionend', () => container.dispatchEvent(new CustomEvent('collect-impressions')))
 * // - OR -
 * container.addEventListener('transitionend', impressionable.collect);
 * // - OR -
 * setTimeout(impressionable.collect, 400)
 */
export class Impressionable {
    constructor(options = {}) {
        if (!env.browser) {
            throw new Error('Impressionable can not be instantiated from a non browser environment');
        }

        this.options = Object.assign(
            {},
            defaults,
            options
        );

        // Other defaults
        this.options.DOMSelector = this.options.DOMSelector || `[${this.options.DOMAttribute}]`;

        const {
            data,
            container,
            DOMSelector,
            excludeDOMSelector,
            dataProperty,
            dataIdentifier
        } = this.options;

        if (!Array.isArray(data)) {
            throw new TypeError(`Impressionable must receive a "data" array argument. Got ${typeof data}`);
        }

        const queryRoot = container === window ? document.body : container;
        if (!queryRoot) {
            throw new Error(`Impressionable container (when passed) expects a DOM element. Got a ${typeof container}`);
        }

        const children = Array.from(queryRoot.querySelectorAll(`${DOMSelector}:not([${VIEWED_ATTRIBUTE}="true"])`));
        const excludedChildren = excludeDOMSelector ? Array.from(queryRoot.querySelectorAll(excludeDOMSelector)) : [];

        const unimpressed = _.without(children, ...excludedChildren);

        Object.assign(
            this,
            {
                container,
                impressed: [],
                unimpressed
            }
        );

        // Convert the data array to a key-value collection for faster lookup
        this.data = this.options.data.reduce(
            (collector, item, $index) => {
                if (dataIdentifier in item) {
                    collector[item[dataIdentifier]] = Object.assign(
                        { $index }, // takes inferiority
                        jsonCopy(dataProperty ? _.get(item, dataProperty) : item)
                    );
                }

                return collector;
            },
            {}
        );

        this.collect = _.throttle(this.collect.bind(this), COLLECT_DELAY, { leading: false });

        this.live();
    }

    /**
     * - Collect the exposed elements' impression data
     * - Move them from "unimpressed" collection to "impressed"
     * - Mark them as "impressed"
     */
    collect() {
        const { container } = this;
        const fullyExposed = container === window || exposed(container, { percent: 100 });
        const events = [];

        // Reducing on a shallow copy. The reduction also mutates the array
        let index = this.unimpressed.length;

        while (index--) {
            const child = this.unimpressed[index];

            if (!exposed(child, { container })) {
                continue;
            }

            // verify it's in the viewport
            if (!(fullyExposed || exposed(child))) {
                continue;
            }

            const impressionData = this.getImpressionData(child);

            if (impressionData) {
                events.push(impressionData);
                child.setAttribute(VIEWED_ATTRIBUTE, 'true');
            } else {
                child.setAttribute(VIEWED_ATTRIBUTE, 'error');
            }

            this.impress(index);
        }

        if (events.length) {
            this.storeImpressions(...events);
            dispatch.throttled();
        }

        return this;
    }

    /**
     * Move an item from "unimpressed" to "impressed" collection.
     * @param {Number} index Index in "unimpressed" collection.
     */
    impress(index) {
        this.impressed.push(...this.unimpressed.splice(index, 1));
        if (!this.unimpressed.length) {
            this.retire();
        }
    }

    /**
     * Retrieve the data item with `id` matching the impression DOMAttribute
     * @param  {HTMLElement} child
     * @return {Object}
     */
    getImpressionData(child) {
        const member = child.getAttribute(this.options.DOMAttribute);
        let data = this.data[member];

        // Enrich data if enrichment method was supplied
        if (typeof this.options.enrich === 'function') {
            const enriched = this.options.enrich(data, child);

            // If enrichment method returns an object, use it
            if (typeof enriched === 'object' || enriched === false) {
                data = enriched;
            }
        }

        return data;
    }

    /**
     * Store impressions in storage (report immediately if unavailable)
     * @param  {...Object} events
     * no return value
     */
    storeImpressions(...events) {
        if (typeof this.options.onImpression === 'function') {
            events.forEach(this.options.onImpression);
        }

        if (!localStorage.available) {
            sendBigQueryEvents(events);
            return;
        }

        this.stored = events;
    }

    get stored() {
        return localStorage.get(STORAGE_KEY) || [];
    }

    set stored(events) {
        const stored = this.stored;

        stored.push(...events);
        localStorage.set(STORAGE_KEY, stored)
            .catch(() => {
                sendBigQueryEvents(...stored);
                localStorage.remove(STORAGE_KEY);
                stats.count('fe_events.error.impressionable', 'localstorage', 1);
            });
    }

    /**
     * Expose the custom event name
     * @type string
     */
    static get CUSTOM_EVENT() {
        return CUSTOM_EVENT;
    }

    /**
     * A list of event to listen to on the container
     * @return {Array}
     */
    get containerEvents() {
        const events = [CUSTOM_EVENT];

        this.container !== window && events.push('scroll', 'touchmove');
        return events;
    }

    /**
     * Attach throttled listeners to container and window events.
     */
    live() {
        this.containerEvents.forEach(
            (event) => this.container.addEventListener(event, this.collect, LISTENER_OPTIONS)
        );

        WINDOW_LISTENERS.forEach(
            (event) => window.addEventListener(event, this.collect, LISTENER_OPTIONS)
        );

        emerge(this.container)
            .then(this.collect)
            .catch((error) => {
                throw error;
            });
    }

    /**
     * Detach listeners from container and window events.
     */
    die() {
        this.containerEvents.forEach(
            (event) => this.container.removeEventListener(event, this.collect, LISTENER_OPTIONS)
        );

        WINDOW_LISTENERS.forEach(
            (event) => window.removeEventListener(event, this.collect, LISTENER_OPTIONS)
        );
    }

    /**
     * Retires the instance, detaching all event listeneres, and clears out redundant references and properties.
     */
    retire() {
        this.retired || Object.assign(this, {
            impressed: [],
            unimpressed: [],
            data: {},
            options: {},
            retired: true
        }).die();
    }
}
